import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl, API_CONFIG } from '../config/api';
import {
  ServerIcon,
  UserIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  StopIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface PostgreSQLProcess {
  pid: number;
  user: string;
  database: string;
  application: string;
  client_addr: string;
  state: string;
  query: string;
  query_start: string;
  backend_start: string;
}

interface PostgreSQLUser {
  username: string;
  superuser: boolean;
  create_db: boolean;
  create_role: boolean;
  valid_until: string;
  conn_limit: number;
}

interface PostgreSQLDatabase {
  name: string;
  owner: string;
  encoding: string;
  collate: string;
  ctype: string;
  conn_limit: number;
  size: string;
}

interface DatabaseInstance {
  id: string;
  name: string;
  host: string;
  port: number;
  status: 'healthy' | 'warning' | 'error';
}

const PostgreSQL: React.FC = () => {
  const [activeTab, setActiveTab] = useState('processes');
  const [processes, setProcesses] = useState<PostgreSQLProcess[]>([]);
  const [users, setUsers] = useState<PostgreSQLUser[]>([]);
  const [databases, setDatabases] = useState<PostgreSQLDatabase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDatabases, setSelectedDatabases] = useState<string[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [availableInstances, setAvailableInstances] = useState<DatabaseInstance[]>([]);

  const tabs = [
    { id: 'processes', name: 'Processes', icon: ServerIcon },
    { id: 'users', name: 'Users', icon: UserIcon },
    { id: 'databases', name: 'Databases', icon: CircleStackIcon },
  ];

  useEffect(() => {
    fetchAvailableInstances();
    fetchData();
    const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [activeTab, selectedDatabases, selectedInstance]);

  const fetchAvailableInstances = async () => {
    try {
      const response = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.DATABASE_INSTANCES));
      setAvailableInstances(response.data.data?.instances || []);
      if (response.data.data?.instances && response.data.data.instances.length > 0 && !selectedInstance) {
        setSelectedInstance(response.data.data.instances[0].id);
      }
    } catch (error) {
      console.error('Error fetching available instances:', error);
    }
  };


  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!selectedInstance) {
        setLoading(false);
        return;
      }

      switch (activeTab) {
        case 'processes':
          const [processesResponse, databasesResponse] = await Promise.all([
            axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_PROCESSES), {
              params: { 
                instance_id: selectedInstance,
                databases: selectedDatabases.length > 0 ? selectedDatabases : undefined 
              }
            }),
            axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_DATABASES), {
              params: { instance_id: selectedInstance }
            })
          ]);
          setProcesses(processesResponse.data.data?.processes || []);
          setDatabases(databasesResponse.data.data?.databases || []);
          break;
        case 'users':
          const usersResponse = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_USERS), {
            params: { instance_id: selectedInstance }
          });
          setUsers(usersResponse.data.data?.users || []);
          break;
        case 'databases':
          const databasesResponse2 = await axios.get(buildApiUrl(API_CONFIG.ENDPOINTS.POSTGRESQL_DATABASES), {
            params: { instance_id: selectedInstance }
          });
          setDatabases(databasesResponse2.data.data?.databases || []);
          break;
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const terminateProcess = async (pid: number) => {
    try {
      await axios.post(buildApiUrl(`${API_CONFIG.ENDPOINTS.POSTGRESQL_TERMINATE_PROCESS}/${pid}/terminate?instance_id=${selectedInstance}`));
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to terminate process');
    }
  };

  const cancelProcess = async (pid: number) => {
    try {
      await axios.post(buildApiUrl(`${API_CONFIG.ENDPOINTS.POSTGRESQL_CANCEL_PROCESS}/${pid}/cancel?instance_id=${selectedInstance}`));
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to cancel process');
    }
  };

  const disableUser = async (username: string) => {
    try {
      await axios.post(buildApiUrl(`/api/postgresql/users/${username}/disable?instance_id=${selectedInstance}`));
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to disable user');
    }
  };

  const enableUser = async (username: string) => {
    try {
      await axios.post(buildApiUrl(`/api/postgresql/users/${username}/enable?instance_id=${selectedInstance}`));
      fetchData(); // Refresh data
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to enable user');
    }
  };

  const renderProcesses = () => (
    <div className="space-y-4">
      {/* Processes Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Active Processes</h3>
        </div>
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">PID</th>
                  <th className="table-header-cell">User</th>
                  <th className="table-header-cell">Database</th>
                  <th className="table-header-cell">State</th>
                  <th className="table-header-cell">Client Address</th>
                  <th className="table-header-cell">Query</th>
                  <th className="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {processes.map((process) => (
                  <tr key={process.pid} className="table-row">
                    <td className="table-cell">{process.pid}</td>
                    <td className="table-cell">
                      <div className="flex items-center">
                        {process.user}
                      </div>
                    </td>
                    <td className="table-cell">{process.database}</td>
                    <td className="table-cell">
                      <span className={`badge ${
                        process.state === 'active' ? 'badge-success' : 'badge-warning'
                      }`}>
                        {process.state || 'idle'}
                      </span>
                    </td>
                    <td className="table-cell">{process.client_addr || '-'}</td>
                    <td className="table-cell">
                      <div className="max-w-xs truncate" title={process.query || ''}>
                        {process.query || '-'}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => cancelProcess(process.pid)}
                          className="btn-warning text-xs"
                          title="Cancel Process"
                        >
                          <StopIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => terminateProcess(process.pid)}
                          className="btn-danger text-xs"
                          title="Terminate Process"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">PostgreSQL Users</h3>
      </div>
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Username</th>
                <th className="table-header-cell">Can Create DB</th>
                <th className="table-header-cell">Superuser</th>
                <th className="table-header-cell">Create Role</th>
                <th className="table-header-cell">Conn Limit</th>
                <th className="table-header-cell">Valid Until</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {users.map((user) => (
                <tr key={user.username} className="table-row">
                  <td className="table-cell">{user.username}</td>
                  <td className="table-cell">
                    <span className={`badge ${user.create_db ? 'badge-success' : 'badge-info'}`}>
                      {user.create_db ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${user.superuser ? 'badge-danger' : 'badge-info'}`}>
                      {user.superuser ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={`badge ${user.create_role ? 'badge-success' : 'badge-info'}`}>
                      {user.create_role ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className="badge badge-info">
                      {user.conn_limit}
                    </span>
                  </td>
                  <td className="table-cell">{user.valid_until || '-'}</td>
                  <td className="table-cell">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => enableUser(user.username)}
                        className="btn-success text-xs"
                        title="Enable User"
                      >
                        <PlayIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => disableUser(user.username)}
                        className="btn-danger text-xs"
                        title="Disable User"
                      >
                        <StopIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderDatabases = () => (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium text-gray-900">PostgreSQL Databases</h3>
      </div>
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Database Name</th>
                <th className="table-header-cell">Owner</th>
                <th className="table-header-cell">Encoding</th>
                <th className="table-header-cell">Collate</th>
                <th className="table-header-cell">CType</th>
                <th className="table-header-cell">Connection Limit</th>
                <th className="table-header-cell">Size</th>
              </tr>
            </thead>
            <tbody className="table-body">
              {databases.map((db) => (
                <tr key={db.name} className="table-row">
                  <td className="table-cell font-medium">{db.name}</td>
                  <td className="table-cell">{db.owner}</td>
                  <td className="table-cell">{db.encoding}</td>
                  <td className="table-cell">{db.collate}</td>
                  <td className="table-cell">{db.ctype}</td>
                  <td className="table-cell">{db.conn_limit}</td>
                  <td className="table-cell">{db.size}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">PostgreSQL Monitoring</h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor and manage PostgreSQL processes, users, and databases
        </p>
      </div>

      {error && (
        <div className="alert-danger">
          <p>{error}</p>
        </div>
      )}

      {/* Instance Selector */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Database Instance</h3>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <select
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select a database instance</option>
              {availableInstances.map((instance) => (
                <option key={instance.id} value={instance.id}>
                  {instance.name} ({instance.host}:{instance.port})
                </option>
              ))}
            </select>
          </div>
          {selectedInstance && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Status:</span>
              {availableInstances.find(i => i.id === selectedInstance)?.status === 'healthy' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Healthy
                </span>
              )}
              {availableInstances.find(i => i.id === selectedInstance)?.status === 'warning' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Warning
                </span>
              )}
              {availableInstances.find(i => i.id === selectedInstance)?.status === 'error' && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Error
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Database Filter */}
      {activeTab === 'processes' && selectedInstance && databases.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow border">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Database Filter</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {databases.map((db) => (
              <label key={db.name} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedDatabases.includes(db.name)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedDatabases([...selectedDatabases, db.name]);
                    } else {
                      setSelectedDatabases(selectedDatabases.filter(d => d !== db.name));
                    }
                  }}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">{db.name}</span>
              </label>
            ))}
          </div>
          {selectedDatabases.length > 0 && (
            <div className="mt-3">
              <button
                onClick={() => setSelectedDatabases([])}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="inline-block h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div>
          {activeTab === 'processes' && renderProcesses()}
          {activeTab === 'users' && renderUsers()}
          {activeTab === 'databases' && renderDatabases()}
        </div>
      )}
    </div>
  );
};

export default PostgreSQL; 